import React, { useState, useEffect, useCallback } from 'react';
import { groceriesAPI, categoriesAPI, suggestionsAPI } from '../services/api';
import AutocompleteInput from './AutocompleteInput';
import './GroceriesTab.css';

const GroceriesTab = ({ houseId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    quantity: '',
    notes: '',
  });
  const [editingItem, setEditingItem] = useState(null);
  const [addingToStock, setAddingToStock] = useState(false);
  const [categories, setCategories] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    if (houseId) {
      loadGroceries();
      loadCategories();
    }
  }, [houseId]);

  const loadCategories = async () => {
    try {
      const response = await categoriesAPI.getAll(houseId);
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  // Debounced search for suggestions
  const [searchTimeout, setSearchTimeout] = useState(null);
  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(async () => {
      try {
        const response = await suggestionsAPI.getSuggestions(query, houseId, 10);
        setSuggestions(response.data);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      }
    }, 300);

    setSearchTimeout(timeout);
  }, [houseId, searchTimeout]);

  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const handleNameChange = (value) => {
    setFormData({ ...formData, name: value });
    fetchSuggestions(value);
  };

  const handleSuggestionSelect = (suggestion) => {
    setFormData({ ...formData, name: suggestion.name });
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const loadGroceries = async () => {
    try {
      const response = await groceriesAPI.getAll(houseId);
      setItems(response.data);
    } catch (error) {
      console.error('Error loading groceries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    try {
      if (editingItem) {
        // Update existing item
        await groceriesAPI.update(editingItem.id, {
          name: formData.name.trim(),
          quantity: formData.quantity || null,
          notes: formData.notes || null,
        });
      } else {
        // Create new item
        await groceriesAPI.create({
          house_id: houseId,
          name: formData.name.trim(),
          quantity: formData.quantity || null,
          notes: formData.notes || null,
        });
      }
      setFormData({ name: '', quantity: '', notes: '' });
      setEditingItem(null);
      setShowForm(false);
      setSuggestions([]);
      setShowSuggestions(false);
      loadGroceries();
    } catch (error) {
      console.error('Error saving grocery item:', error);
      alert('Failed to save grocery item');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      quantity: item.quantity || '',
      notes: item.notes || '',
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setFormData({ name: '', quantity: '', notes: '' });
    setEditingItem(null);
    setShowForm(false);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleToggle = async (id) => {
    try {
      await groceriesAPI.toggle(id);
      loadGroceries();
    } catch (error) {
      console.error('Error toggling grocery item:', error);
      alert('Failed to update grocery item');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await groceriesAPI.delete(id);
        loadGroceries();
      } catch (error) {
        console.error('Error deleting grocery item:', error);
        alert('Failed to delete grocery item');
      }
    }
  };

  const handleClearPurchased = async () => {
    if (window.confirm('Remove all purchased items from the list?')) {
      try {
        const purchasedItems = items.filter(item => item.is_purchased);
        await Promise.all(purchasedItems.map(item => groceriesAPI.delete(item.id)));
        loadGroceries();
      } catch (error) {
        console.error('Error clearing purchased items:', error);
        alert('Failed to clear purchased items');
      }
    }
  };

  const handleAddToStock = async () => {
    const purchasedItems = items.filter(item => item.is_purchased);
    if (purchasedItems.length === 0) {
      alert('No purchased items to add to stock');
      return;
    }

    if (!window.confirm(`Add ${purchasedItems.length} purchased item(s) to stock?`)) {
      return;
    }

    setAddingToStock(true);
    try {
      // Get default category (most recently used or first available)
      const defaultCategoryId = categories.length > 0 ? categories[0].id : null;
      
      const groceryIds = purchasedItems.map(item => item.id);
      const response = await groceriesAPI.addToStock(groceryIds, defaultCategoryId);
      
      alert(`Successfully added ${response.data.added} new item(s) and updated ${response.data.updated} existing item(s) to stock!`);
      
      // Optionally clear purchased items after adding to stock
      if (window.confirm('Remove purchased items from grocery list?')) {
        await Promise.all(purchasedItems.map(item => groceriesAPI.delete(item.id)));
      }
      
      loadGroceries();
    } catch (error) {
      console.error('Error adding to stock:', error);
      alert('Failed to add items to stock: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAddingToStock(false);
    }
  };

  if (loading) {
    return <div className="groceries-loading">Loading grocery list...</div>;
  }

  if (!houseId) {
    return <div className="groceries-message">Please select a house</div>;
  }

  const purchasedCount = items.filter(item => item.is_purchased).length;
  const totalCount = items.length;

  return (
    <div className="groceries-tab">
      <div className="groceries-header">
        <div>
          <h2>🛒 Grocery List</h2>
          {totalCount > 0 && (
            <p className="groceries-stats">
              {purchasedCount} of {totalCount} items purchased
            </p>
          )}
        </div>
        <div className="header-actions">
          {purchasedCount > 0 && (
            <>
              <button 
                className="btn-add-to-stock" 
                onClick={handleAddToStock}
                disabled={addingToStock}
              >
                {addingToStock ? 'Adding...' : '📦 Add to Stock'}
              </button>
              <button className="btn-clear" onClick={handleClearPurchased}>
                Clear Purchased
              </button>
            </>
          )}
          <button className="btn-add" onClick={() => {
            setEditingItem(null);
            setFormData({ name: '', quantity: '', notes: '' });
            setShowForm(true);
          }}>
            + Add Item
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="grocery-form">
          <h3>{editingItem ? 'Edit Grocery Item' : 'Add Grocery Item'}</h3>
          <div className="form-row">
            <div style={{ position: 'relative', width: '100%' }}>
              <AutocompleteInput
                value={formData.name}
                onChange={handleNameChange}
                onSelect={handleSuggestionSelect}
                suggestions={showSuggestions && formData.name.length > 0 && !editingItem ? suggestions : []}
                placeholder="Item name *"
                required
                disabled={!!editingItem}
              />
            </div>
            <input
              type="text"
              placeholder="Quantity (e.g., 2 kg, 1 bottle)"
              value={formData.quantity}
              onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
            />
          </div>
          <textarea
            placeholder="Notes (optional)"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows="2"
          />
          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={handleCancel}>
              Cancel
            </button>
            <button type="submit" className="btn-submit">{editingItem ? 'Update' : 'Add'}</button>
          </div>
        </form>
      )}

      {items.length === 0 ? (
        <div className="groceries-empty">
          <p>Your grocery list is empty.</p>
          <p>Click "+ Add Item" to start adding items to your shopping list!</p>
        </div>
      ) : (
        <div className="groceries-list">
          {items.map((item) => (
            <div
              key={item.id}
              className={`grocery-item ${item.is_purchased ? 'purchased' : ''}`}
            >
              <div className="grocery-item-content">
                <label className="grocery-checkbox-label">
                  <input
                    type="checkbox"
                    checked={item.is_purchased}
                    onChange={() => handleToggle(item.id)}
                    className="grocery-checkbox"
                  />
                  <span className="grocery-item-name">{item.name}</span>
                </label>
                {item.quantity && (
                  <span className="grocery-quantity">{item.quantity}</span>
                )}
                {item.notes && (
                  <span className="grocery-notes">{item.notes}</span>
                )}
              </div>
              <div className="grocery-item-actions">
                <button
                  className="btn-edit-small"
                  onClick={() => handleEdit(item)}
                  title="Edit item"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
                <button
                  className="btn-delete-small"
                  onClick={() => handleDelete(item.id)}
                  title="Delete item"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GroceriesTab;

